import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { PaymentBrand } from "@/components/PaymentBrand";
import CheckoutSheet from "@/components/checkout/CheckoutSheet";
import LoadError from "@/components/ui/LoadError";
import PageContainer from "@/components/ui/PageContainer";

/* PRYMEBET redesign — Coin Store (design "04 Wallet & Store" — WA-02 Get Coins):
   offer picker, selectable packs + custom amount, and a sticky Order summary rail.
   Reuses the real /payments/packages + /payments/checkout flow (same as Wallet).

   Wave 2 premium polish (PREMIUM_UI_DIRECTION): shared PageContainer gutters/rhythm,
   tokenized surfaces, focus-ring on every control, tabular-nums on all amounts, a
   layout-matching load skeleton, honest "real-terms" offer copy (no "TBD"), and an
   Accepted row that reflects only what actually works today (Card via Stripe). The
   actual checkout, bonus math, and payment behavior are unchanged — this is visual. */

// Honest, real-terms one-liner for a deposit-match offer. Mirrors the server's credit
// math (usd × match_pct, capped at max_bonus) so we never print a vague "TBD".
function offerTerms(p) {
  if (p.description) return p.description;
  const pct = Number(p.match_pct) > 0 ? Number(p.match_pct) : null;
  const cap = Number(p.max_bonus) > 0 ? Number(p.max_bonus) : null;
  const min = Number(p.min_deposit) > 0 ? Number(p.min_deposit) : null;
  const scope = p.restrict === "live_casino" ? "Live Dealer games · " : "";
  if (pct) {
    return `${scope}${pct}% bonus${cap ? ` up to $${cap}` : ""}${min ? ` · min $${min}` : ""}`;
  }
  // No percentage from the backend → state honestly that it's finalized at checkout.
  return `${scope}Bonus finalized at checkout${min ? ` · min $${min}` : ""}`;
}

// §7 one-shot staggered entrance (reduced-motion guarded by the caller — static fallback).
const SECTION_STAGGER = { show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const SECTION_ITEM = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

export default function CoinStore() {
  const { user, streamerMode } = useAuth();
  const reduceMotion = useReducedMotion();
  const [packages, setPackages] = useState([]);
  const [methods, setMethods] = useState({ stripe: true, razorpay: false });
  const [firstBonus, setFirstBonus] = useState({ available: false, coinPct: 0, cashPct: 0 });
  const [customCfg, setCustomCfg] = useState({ min_usd: 5, max_usd: 1000, coins_per_usd: 1000, cash_per_usd: 1 });
  const [customUsd, setCustomUsd] = useState("");
  const [promos, setPromos] = useState([]);
  const [chosenPromo, setChosenPromo] = useState(""); // "" = no offer
  const [selected, setSelected] = useState(null); // lookup_key or "custom"
  const [checkout, setCheckout] = useState(null); // { order, promoId } — opens the branded sheet
  const [pkgErr, setPkgErr] = useState(false); // distinct load-failure (UIA-02) vs an empty catalog
  const [loading, setLoading] = useState(true); // true until the first /payments/packages resolves
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setPkgErr(false);
    setLoading(true);
    api.get("/payments/packages").then(({ data }) => {
      const pkgs = data.packages || [];
      setPackages(pkgs);
      setMethods(data.methods || { stripe: true, razorpay: false });
      setFirstBonus({ available: !!data.first_purchase_available, coinPct: data.first_purchase_coin_pct || 0, cashPct: data.first_purchase_cash_pct || 0 });
      if (data.custom) setCustomCfg(data.custom);
      if (pkgs.length) setSelected(pkgs[Math.min(2, pkgs.length - 1)].lookup_key); // default a mid pack
    }).catch(() => setPkgErr(true)).finally(() => setLoading(false));
    api.get("/promotions").then(({ data }) => setPromos(data)).catch(() => {});
  }, [reloadTick]);

  const depositPromos = promos.filter((p) => p.promo_type === "deposit_match");
  const isCustom = selected === "custom";
  const selPkg = packages.find((p) => p.lookup_key === selected) || null;
  const customV = parseFloat(customUsd) || 0;
  const summary = isCustom
    ? { label: "Custom amount", usd: customV, coins: Math.round(customV * customCfg.coins_per_usd), cash: +(customV * customCfg.cash_per_usd).toFixed(2) }
    : selPkg
    ? { label: selPkg.label, usd: selPkg.usd, coins: selPkg.coins, cash: selPkg.cash }
    : null;

  // Open the branded in-app CheckoutSheet (Stripe Elements) instead of redirecting
  // to Stripe's hosted page. Builds the order from the selected pack / custom amount.
  const buy = () => {
    const lookup_key = isCustom ? "pnr_custom" : selected;
    let custom_usd = null;
    if (isCustom) {
      if (!customV || customV < customCfg.min_usd || customV > customCfg.max_usd) {
        toast.error(`Enter an amount between $${customCfg.min_usd} and $${customCfg.max_usd}`); return;
      }
      custom_usd = Math.round(customV * 100) / 100;
    }
    if (!lookup_key || !summary) { toast.error("Choose a coin pack"); return; }
    setCheckout({
      order: {
        lookup_key,
        custom_usd,
        label: summary.label,
        usd: summary.usd,
        coins: summary.coins,
        cash: summary.cash,
        firstBonusAvailable: firstBonus.available,
        firstBonusCoins: firstBonus.available ? Math.round(summary.coins * firstBonus.coinPct / 100) : 0,
        firstBonusCash: firstBonus.available ? +(summary.cash * firstBonus.cashPct / 100).toFixed(2) : 0,
      },
      promoId: chosenPromo || null,
    });
  };

  const canPay = summary && summary.usd >= customCfg.min_usd;

  // Accepted methods — reflect only what actually works today. Card (Stripe) is the single
  // functional path (CheckoutSheet marks Apple/Google/PayPal/Venmo/Crypto "coming soon"), so
  // we advertise the card brands we actually accept rather than overclaiming wallets/PayPal.
  const acceptedBrands = ["visa", "mastercard", "amex"];

  return (
    <PageContainer clearBottomNav>
      <div className="flex items-center justify-between pt-1">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00D632]">Coin Store</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Get coins</h1>
          <p className="mt-1.5 text-sm text-meta">Every pack includes <b className="text-white">free PCash</b> — the redeemable sweepstakes currency.</p>
        </div>
        <Link to="/wallet" className="focus-ring hidden items-center gap-1.5 rounded-full border border-surface-border bg-surface-5 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:border-border-strong sm:inline-flex">
          <Icon name="arrow-left" size={14} /> Wallet
        </Link>
      </div>

      {/* §7 one-shot staggered entrance — balances strip then the packs/summary grid.
          Owns its own rhythm so PageContainer still spaces it from the header. */}
      <motion.div
        className="space-y-6 lg:space-y-8"
        variants={reduceMotion ? undefined : SECTION_STAGGER}
        initial={reduceMotion ? false : "hidden"}
        animate="show"
      >

      {/* balances strip — primary read-out BEFORE the promotional packs (§1.4) */}
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="grid grid-cols-2 gap-3">
        <div className="surface-well rounded-2xl p-4">
          <div className="flex items-center gap-1.5">
            <Icon name="pnrcoin" size={16} className="text-pnr-coin" />
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#71717a]">PCoins · play</p>
          </div>
          <p className="mt-1 font-display text-2xl font-black tabular-nums text-[#00D632]">{streamerMode ? "•••" : Number(user?.coins || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          <p className="text-xs text-meta">For fun play. Not redeemable.</p>
        </div>
        <div className="surface-well rounded-2xl p-4">
          <div className="flex items-center gap-1.5">
            <Icon name="pnrcash" size={16} className="text-pnr-cash" />
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#71717a]">PCash · prizes</p>
          </div>
          <p className="mt-1 font-display text-2xl font-black tabular-nums text-white">{streamerMode ? "•••" : `$${Number(user?.cash || 0).toFixed(2)}`}</p>
          <p className="text-xs text-meta">Redeems for prizes 1:1.</p>
        </div>
      </motion.div>

      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* left: offer + packs */}
        <div className="space-y-6 lg:col-span-8">
          {/* first-purchase bonus */}
          {firstBonus.available && (
            <div className="flex items-start gap-3 rounded-2xl border border-[#00D632]/25 bg-[#00D632]/[0.06] p-4" data-testid="store-first-purchase">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#00D632]/15"><Icon name="sparkles" size={20} className="text-[#00D632]" /></span>
              <div>
                <p className="font-display text-sm font-extrabold text-white">First-purchase bonus unlocked <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-meta">One time only</span></p>
                <p className="text-sm text-meta">Your very first pack adds <b className="text-white tabular-nums">+{firstBonus.coinPct}% PCoins</b> and <b className="text-white tabular-nums">+{firstBonus.cashPct}% PCash</b> — automatically.</p>
              </div>
            </div>
          )}

          {/* choose your offer */}
          {depositPromos.length > 0 && (
            <section className="space-y-2" data-testid="store-offers">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#71717a]">Choose your offer</h2>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {depositPromos.map((p) => {
                  const sel = chosenPromo === p.id;
                  return (
                    <button key={p.id} type="button" data-testid={`store-offer-${p.id}`} onClick={() => setChosenPromo(p.id)}
                      aria-pressed={sel}
                      className={`focus-ring rounded-2xl border p-3 text-left transition-colors ${sel ? "border-[#00D632] bg-[#00D632]/[0.06] shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "surface-premium hover:border-[#00D632]/50"}`}>
                      <div className="flex items-center gap-1.5">
                        {sel && <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#00D632] text-[9px] font-black text-[#0A0A0A]"><Icon name="check" size={11} /></span>}
                        <p className={`font-display text-sm font-black ${sel ? "text-[#00D632]" : "text-white"}`}>{p.title || `${p.match_pct}% deposit match`}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-meta">{offerTerms(p)}</p>
                    </button>
                  );
                })}
                <button type="button" data-testid="store-offer-none" onClick={() => setChosenPromo("")}
                  aria-pressed={chosenPromo === ""}
                  className={`focus-ring rounded-2xl border p-3 text-left transition-colors ${chosenPromo === "" ? "border-[#00D632] bg-[#00D632]/[0.06] shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "surface-premium hover:border-[#00D632]/50"}`}>
                  <p className="font-display text-sm font-black text-white">No offer</p>
                  <p className="mt-0.5 text-xs text-meta">Just the coins</p>
                </button>
              </div>
            </section>
          )}

          {/* packs */}
          <section className="space-y-3">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#71717a]">Choose your pack</h2>
            {loading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="store-skeleton" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col gap-3 rounded-2xl border border-surface-border bg-surface-3 p-5">
                    <span className="h-3 w-16 animate-pulse rounded bg-surface-4" />
                    <span className="h-8 w-28 animate-pulse rounded bg-surface-4" />
                    <span className="h-4 w-24 animate-pulse rounded bg-surface-4" />
                    <span className="mt-1 h-9 w-20 animate-pulse rounded-full bg-surface-4" />
                  </div>
                ))}
              </div>
            ) : pkgErr ? (
              <LoadError onRetry={() => setReloadTick((t) => t + 1)} message="We couldn't load the coin packs." testid="store-load-error" />
            ) : packages.length ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {packages.map((p) => {
                  const sel = selected === p.lookup_key;
                  return (
                    <button key={p.lookup_key} type="button" data-testid={`store-pkg-${p.lookup_key}`} onClick={() => setSelected(p.lookup_key)}
                      aria-pressed={sel}
                      className={`focus-ring relative flex flex-col gap-2 rounded-2xl border p-5 text-left transition-colors ${sel ? "border-[#00D632] bg-[#00D632]/[0.05] shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "surface-premium hover:border-[#00D632]/50"}`}>
                      {sel && (
                        <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-[#00D632] text-xs font-black text-[#0A0A0A]"><Icon name="check" size={14} /></span>
                      )}
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#71717a]">{p.label}</p>
                      <p className="flex items-baseline gap-1.5 font-display text-3xl font-black tabular-nums tracking-tight text-white">
                        {Number(p.coins).toLocaleString()}
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#71717a]">coins</span>
                      </p>
                      <p className="flex items-center gap-1.5 text-sm font-semibold text-[#00D632]">
                        <Icon name="pnrcash" size={14} className="text-pnr-cash" />
                        + FREE <span className="tabular-nums">${Number(p.cash).toFixed(2)}</span> PCash
                      </p>
                      {firstBonus.available ? (
                        <p className="text-xs tabular-nums text-[#00D632]">
                          First-purchase bonus: +{firstBonus.coinPct}% PCoins
                          {firstBonus.cashPct ? ` · +${firstBonus.cashPct}% PCash` : ""}
                        </p>
                      ) : null}
                      <span className={`mt-1 inline-flex items-center justify-center rounded-full px-4 py-2 font-display text-sm font-black tabular-nums ${sel ? "bg-[#00D632] text-[#0A0A0A]" : "bg-surface-5 text-white"}`}>
                        ${Number(p.usd).toFixed(2)}
                      </span>
                    </button>
                  );
                })}

                {/* custom amount card */}
                <div data-testid="store-pkg-custom"
                  className={`flex flex-col gap-2 rounded-2xl border border-dashed p-5 transition-colors ${isCustom ? "border-[#00D632] bg-[#00D632]/[0.05] shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)]" : "border-surface-border bg-surface-3"}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#71717a]">Custom amount</p>
                  <div className="flex items-center rounded-full border border-surface-border bg-surface-4 px-4 focus-within:border-[#00D632]/50">
                    <span className="text-[#71717a]">$</span>
                    <input
                      value={customUsd}
                      onChange={(e) => { setCustomUsd(e.target.value.replace(/[^0-9.]/g, "")); setSelected("custom"); }}
                      onFocus={() => setSelected("custom")}
                      inputMode="decimal"
                      placeholder={`${customCfg.min_usd}`}
                      data-testid="store-custom-usd" name="custom_usd"
                      aria-label="Custom amount in dollars"
                      className="w-full bg-transparent px-2 py-2.5 tabular-nums text-white outline-none placeholder:text-[#52525b]"
                    />
                  </div>
                  {customV > 0 ? (
                    <p className="text-xs text-meta">You get <b className="tabular-nums text-[#00D632]">{Math.round(customV * customCfg.coins_per_usd).toLocaleString()} PCoins</b> + <span className="tabular-nums">${(customV * customCfg.cash_per_usd).toFixed(2)}</span> PCash</p>
                  ) : (
                    <p className="text-xs tabular-nums text-meta">${customCfg.min_usd}–${customCfg.max_usd} · {customCfg.coins_per_usd.toLocaleString()} PCoins + ${customCfg.cash_per_usd}/$1</p>
                  )}
                  <button type="button" data-testid="store-select-custom" onClick={() => setSelected("custom")}
                    aria-pressed={isCustom}
                    className={`focus-ring mt-auto inline-flex items-center justify-center rounded-full px-4 py-2 font-display text-sm font-black tabular-nums ${isCustom ? "bg-[#00D632] text-[#0A0A0A]" : "bg-surface-5 text-white"}`}>
                    {isCustom && customV > 0 ? `Selected · $${customV.toFixed(2)}` : "Select custom"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-surface-border bg-surface-2 p-8 text-center text-sm text-meta" data-testid="store-empty">Coin packs aren't available right now. Please check back soon.</div>
            )}
          </section>
        </div>

        {/* right: order summary rail */}
        <aside className="lg:col-span-4">
          <div className="space-y-4 lg:sticky lg:top-24" data-testid="store-order-summary">
            <div className="surface-premium rounded-2xl p-5">
              <h2 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">Order summary</h2>
              {summary ? (
                <>
                  <div className="mt-4 space-y-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">{summary.label}</span>
                      <span className="font-display font-black tabular-nums text-white">${Number(summary.usd || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-meta">PCoins</span>
                      <span className="font-semibold tabular-nums text-[#00D632]">{Number(summary.coins || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-meta">Free PCash</span>
                      <span className="font-semibold tabular-nums text-white">${Number(summary.cash || 0).toFixed(2)}</span>
                    </div>
                    {firstBonus.available && (
                      <div className="flex items-center justify-between">
                        <span className="text-meta">First-purchase bonus</span>
                        <span className="font-semibold tabular-nums text-[#00D632]">
                          +{Math.round((summary.coins || 0) * firstBonus.coinPct / 100).toLocaleString()} PCoins
                          {firstBonus.cashPct ? ` · +$${((summary.cash || 0) * firstBonus.cashPct / 100).toFixed(2)}` : ""}
                        </span>
                      </div>
                    )}
                    {chosenPromo && (() => {
                      // Show the selected offer's real match where the backend gives us a match_pct.
                      // The deposit-match grant is dollar-denominated bonus funds (usd × match_pct),
                      // capped at max_bonus — the same math server.py applies on credit. Anything
                      // without a real percentage stays an honest "finalized at checkout".
                      const promo = depositPromos.find((p) => p.id === chosenPromo);
                      const pct = promo && Number(promo.match_pct) > 0 ? Number(promo.match_pct) : null;
                      let bonusUsd = pct ? (summary.usd || 0) * pct / 100 : 0;
                      const cap = promo && Number(promo.max_bonus) > 0 ? Number(promo.max_bonus) : 0;
                      if (cap > 0) bonusUsd = Math.min(bonusUsd, cap);
                      return (
                        <div className="flex items-center justify-between">
                          <span className="text-meta">{promo?.title || "Purchase bonus"}</span>
                          <span className={`font-semibold tabular-nums ${pct ? "text-[#00D632]" : "text-meta"}`}>
                            {pct ? `+$${bonusUsd.toFixed(2)} bonus funds` : "Finalized at checkout"}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="mt-4 border-t border-surface-border pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-meta">You receive</span>
                      <span className="font-display text-xl font-black tabular-nums text-[#00D632]">{Number(summary.coins || 0).toLocaleString()} coins</span>
                    </div>
                    <p className="mt-0.5 text-right text-sm font-semibold tabular-nums text-white">${Number(summary.cash || 0).toFixed(2)} PCash</p>
                  </div>
                </>
              ) : (
                <p className="mt-4 text-sm text-meta">Select a coin pack to see your order.</p>
              )}

              {/* Card is the single functional method today (Stripe). The one green primary CTA
                  opens the branded checkout sheet (which hosts the real card form + any wallets
                  Stripe offers there). We don't render separate Apple/Google buttons here because
                  those express methods are "coming soon" in the sheet — showing them as live CTAs
                  would overclaim. */}
              <button
                data-testid="store-pay"
                onClick={buy}
                disabled={!canPay}
                className="focus-ring mt-5 w-full rounded-full bg-[#00D632] py-3.5 font-display font-black tabular-nums text-[#0A0A0A] shadow-[var(--elev-2)] transition-colors hover:bg-[#00B829] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {summary ? `Pay $${Number(summary.usd || 0).toFixed(2)} with card` : "Choose a pack"}
              </button>
              <p className="mt-2 text-center text-xs text-[#71717a]">
                Secure checkout powered by Stripe · $5 minimum ·{" "}
                <Link to="/purchases-refunds" data-testid="store-purchases-policy-link" className="focus-ring text-[#00D632] underline underline-offset-2 hover:text-white">Purchases &amp; Refunds</Link>
              </p>
            </div>

            <div className="surface-premium rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#71717a]">Accepted</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {acceptedBrands.map((m) => (
                  <div key={m} className="flex h-10 items-center rounded-xl border border-surface-border bg-surface-5 px-3">
                    <PaymentBrand brand={m} height={18} />
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-[#71717a]">
                Pay by debit or credit card. More methods are coming soon.{" "}
                <Link to="/free-entry" data-testid="store-free-entry-link" className="focus-ring text-[#00D632] underline underline-offset-2 hover:text-white">No purchase necessary</Link>{" "}to play or win. PCoins are for entertainment; PCash redeems for prizes. 18+.
              </p>
            </div>
          </div>
        </aside>
      </motion.div>
      </motion.div>

      <CheckoutSheet
        open={!!checkout}
        order={checkout?.order}
        promoId={checkout?.promoId}
        onClose={() => setCheckout(null)}
      />
    </PageContainer>
  );
}
